import { NodeType } from '@/enums/node-type.enum';
import { sanitizeRecipe } from '../Nodes/Utils';
import type { BlobstoreAssetRef, NodeId } from 'web';
import type { Edge, Node } from '@/types/node';

/** The shape `buildNodeClipboardPayload` writes and the paste handler reads back. */
export interface NodeClipboardPayload {
  workflowId: string;
  nodes?: Node[];
  edges?: Edge[];
}

/**
 * Serializes `nodeIds` — plus any not-included children of group nodes among them, plus the edges
 * that run between the resulting set — into the JSON payload the canvas paste handler reads.
 * `workflowId` rides along so a paste elsewhere can tell the copy was cross-workflow.
 *
 * @returns The clipboard string, or `null` when there is nothing to write.
 */
export function buildNodeClipboardPayload(
  nodeIds: NodeId[],
  nodes: Node[],
  edges: Edge[],
  getChildNodesOfGroup: (groupId: NodeId) => Node[],
  workflowId: string | undefined,
): string | null {
  const idsToCopy = new Set(nodeIds);
  const nodesToCopy = nodes.filter((node) => idsToCopy.has(node.id));
  if (nodesToCopy.length === 0) return null;

  const nodesIncludingChildren = nodesToCopy.flatMap((node) => {
    if (node.type === NodeType.CustomGroup) {
      const nonIncludedChildren = getChildNodesOfGroup(node.id).filter((childNode) => !idsToCopy.has(childNode.id));
      return [node, ...nonIncludedChildren];
    }
    return node;
  });

  const nodesIncludingChildrenIdSet = new Set(nodesIncludingChildren.map((node) => node.id));

  // Copy edges that connect between copied nodes
  const edgesToCopy = edges.filter(
    (edge) => nodesIncludingChildrenIdSet.has(edge.source) && nodesIncludingChildrenIdSet.has(edge.target),
  );

  const { nodes: nodesToWrite, edges: edgesToWrite } = sanitizeRecipe(nodesIncludingChildren, edgesToCopy);

  return JSON.stringify({ workflowId, nodes: nodesToWrite, edges: edgesToWrite });
}

/** Every Blobstore asset id a payload's refs carry, deduplicated. */
export function assetIdsIn(payload: unknown): string[] {
  const ids = new Set<string>();
  const walk = (value: unknown): void => {
    if (typeof value === 'string') {
      parseRefLines(value)?.forEach(walk);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(walk);
      return;
    }
    if (!value || typeof value !== 'object') return;
    const id = (value as { blobstore?: { id?: unknown } }).blobstore?.id;
    if (typeof id === 'string') ids.add(id);
    Object.values(value).forEach(walk);
  };
  walk(payload);
  return [...ids];
}

// TODO
/**
 * A copy of `payload` with each ref replaced by its copy in `copiedRefs` (new id and creation time). A ref
 * mapped to `null` loses its Blobstore field, and a JSON-string one becomes its plain url; refs it does not
 * name are kept.
 */
export function remapAssetIds<T>(payload: T, copiedRefs: Record<string, BlobstoreAssetRef | null>): T {
  const remapLine = (parsed: unknown): string => {
    const ref = parsed as { url?: unknown; blobstore?: { id?: unknown } } | null;
    if (typeof ref?.blobstore?.id === 'string' && copiedRefs[ref.blobstore.id] === null) {
      return typeof ref.url === 'string' ? ref.url : '';
    }
    return JSON.stringify(walk(parsed));
  };

  const walk = (value: unknown): unknown => {
    if (typeof value === 'string') {
      const lines = parseRefLines(value);
      if (!lines) return value;
      const raw = value.split('\n');
      return lines.map((parsed, i) => (parsed === undefined ? raw[i] : remapLine(parsed))).join('\n');
    }
    if (Array.isArray(value)) return value.map(walk);
    if (!value || typeof value !== 'object') return value;
    const next = Object.fromEntries(Object.entries(value).map(([key, child]) => [key, walk(child)]));
    const blobstore = next.blobstore as { id?: unknown } | undefined;
    const copiedRef = typeof blobstore?.id === 'string' ? copiedRefs[blobstore.id] : undefined;
    if (copiedRef === null) {
      delete next.blobstore;
    } else if (copiedRef) {
      next.blobstore = copiedRef;
    }
    return next;
  };

  return walk(payload) as T;
}

/**
 * Model run inputs persist file refs as JSON strings, one per line for a multi-file input. Each line's
 * parsed ref, or undefined for a line that is not one; undefined when no line carries a ref.
 */
function parseRefLines(value: string): unknown[] | undefined {
  if (!value.includes('"blobstore"')) return undefined;
  const lines = value.split('\n').map(parseRefLine);
  return lines.some((line) => line !== undefined) ? lines : undefined;
}

function parseRefLine(line: string): unknown {
  if (!line.includes('"blobstore"')) return undefined;
  try {
    return JSON.parse(line);
  } catch {
    return undefined;
  }
}

export function isCrossWorkflow(sourceWorkflowId: string, destinationWorkflowId: string): boolean {
  return Boolean(sourceWorkflowId && destinationWorkflowId && sourceWorkflowId !== destinationWorkflowId);
}
